import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import { SessionMessage, SessionMessageCreate } from '@crowdctrl/core';

/**
 * Service for session message management API calls.
 * Manages session messages stored in Firestore subcollection:
 * /tenants/{tenantId}/workspaces/{workspaceId}/sessions/{sessionId}/messages/{messageId}
 */
@Injectable({
  providedIn: 'root',
})
export class SessionMessageService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get all messages for a session with optional pagination
   */
  getSessionMessages(
    sessionId: string,
    workspaceId: string,
    tenantId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    messages: SessionMessage[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      tenantId,
      workspaceId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        messages: SessionMessage[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/session_messages_api/api/sessions/${sessionId}/messages`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Send a message to a session
   */
  sendSessionMessage(
    sessionId: string,
    workspaceId: string,
    tenantId: string,
    content: string,
    messageType: 'message' | 'help_request' | 'assist_request',
  ): Observable<{ message: SessionMessage }> {
    const messageData: SessionMessageCreate = {
      sessionId,
      workspaceId,
      tenantId,
      content,
      messageType,
    };
    return from(
      this.apiService.post<{ message: SessionMessage }, SessionMessageCreate>(
        this.baseUrl,
        `/session_messages_api/api/sessions/${sessionId}/messages?tenantId=${encodeURIComponent(tenantId)}&workspaceId=${encodeURIComponent(workspaceId)}`,
        messageData,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Mark a message as read by the current user
   */
  markMessageRead(
    sessionId: string,
    messageId: string,
    userId: string,
    workspaceId: string,
    tenantId: string,
  ): Observable<{ message: SessionMessage }> {
    const body: Record<string, never> = {};
    return from(
      this.apiService.put<{ message: SessionMessage }, Record<string, never>>(
        this.baseUrl,
        `/session_messages_api/api/sessions/${sessionId}/messages/${messageId}/read?tenantId=${encodeURIComponent(tenantId)}&workspaceId=${encodeURIComponent(workspaceId)}&userId=${encodeURIComponent(userId)}`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }
}
